import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Brain,
  BookOpen,
  Bookmark,
  BookmarkCheck,
  ChevronRight,
  FolderPlus,
  Pencil,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import VocabLayout from "./VocabLayout";
import { HSK_COLORS } from "./constants";
import {
  getMasterTopics,
  getMasterVocabList,
  getUserVocabs,
  saveVocab,
  deleteUserVocab,
  getFolders,
  createFolder,
  updateFolder,
  deleteFolder,
  addToFolder,
} from "../../services/UserVocabService";

const LIMIT = 24;

// ── tiny helpers ──────────────────────────────────────────────────────────────

function HskBadge({ level }) {
  const color = HSK_COLORS[level] || "bg-gray-100 text-gray-600";
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${color}`}>
      HSK {level}
    </span>
  );
}

// ── MasterWordCard — used when browsing a topic ───────────────────────────────
function MasterWordCard({ word, saved, onSave, onUnsave, savingId, checked, onToggle }) {
  const busy = savingId === word._id;
  return (
    <div
      onClick={() => onToggle(word._id)}
      className={`bg-white rounded-2xl border shadow-sm p-4 flex flex-col gap-2 cursor-pointer transition-all ${
        checked
          ? "border-red-300 ring-2 ring-red-200 shadow-md -translate-y-0.5"
          : "border-gray-100 hover:shadow-md"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <span className="text-xl font-bold text-gray-900">{word.word}</span>
          {word.pinyin && (
            <span className="ml-2 text-sm text-gray-400 italic">{word.pinyin}</span>
          )}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            saved ? onUnsave(word._id) : onSave(word._id);
          }}
          disabled={busy}
          className={`shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-xl text-xs font-medium transition-all disabled:opacity-50 ${
            saved
              ? "bg-red-50 text-red-600 hover:bg-red-100"
              : "bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-600"
          }`}
        >
          {saved ? <BookmarkCheck size={13} /> : <Bookmark size={13} />}
          {busy ? "..." : saved ? "Đã lưu" : "Lưu"}
        </button>
      </div>
      <p className="text-base text-gray-700">{word.meaning}</p>
      {word.example && (
        <p className="text-sm text-gray-400 italic border-l-2 border-gray-100 pl-2">{word.example}</p>
      )}
      <div className="mt-auto pt-1">
        <HskBadge level={word.hskLevel} />
      </div>
    </div>
  );
}

// ── SavedWordCard — used when viewing saved/folder words ──────────────────────
function SavedWordCard({ item, onUnsave, checked, onToggle }) {
  const master = item.masterVocabId;
  if (!master) return null;
  return (
    <div
      onClick={() => onToggle(item._id)}
      className={`bg-white rounded-2xl border shadow-sm p-4 flex flex-col gap-2 cursor-pointer transition-all ${
        checked
          ? "border-blue-300 ring-2 ring-blue-200 shadow-md -translate-y-0.5"
          : "border-gray-100 hover:shadow-md"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <span className="text-xl font-bold text-gray-900">{master.word}</span>
          {master.pinyin && (
            <span className="ml-2 text-sm text-gray-400 italic">{master.pinyin}</span>
          )}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onUnsave(item._id);
          }}
          className="shrink-0 p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
          title="Xóa khỏi danh sách"
        >
          <Trash2 size={14} />
        </button>
      </div>
      <p className="text-base text-gray-700">{master.meaning}</p>
      {master.example && (
        <p className="text-sm text-gray-400 italic border-l-2 border-gray-100 pl-2">{master.example}</p>
      )}
      <div className="mt-auto pt-1 flex items-center gap-2">
        <HskBadge level={master.hskLevel} />
        {master.topicId?.name && (
          <span className="text-xs text-gray-400">{master.topicId.icon || ""} {master.topicId.name}</span>
        )}
      </div>
    </div>
  );
}

// ── FolderRenameInline ────────────────────────────────────────────────────────
function FolderRenameInline({ folder, onSave, onCancel }) {
  const [name, setName] = useState(folder.name);
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  return (
    <div className="flex items-center gap-1 px-2 py-1">
      <input
        ref={inputRef}
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") onSave(name);
          if (e.key === "Escape") onCancel();
        }}
        className="flex-1 text-sm border-b border-red-400 outline-none bg-transparent py-0.5"
      />
      <button onClick={() => onSave(name)} className="text-green-600"><X size={13} style={{ transform: "rotate(45deg)" }} /></button>
      <button onClick={onCancel} className="text-gray-400"><X size={13} /></button>
    </div>
  );
}

function PracticeStartModal({
  open,
  onClose,
  scope,
  scopeId,
  onScopeChange,
  onScopeIdChange,
  preview,
  previewTotal,
  previewLoading,
  folders,
  topics,
  onStart,
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-xl shadow-xl">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-semibold">Chọn nguồn luyện tập</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-2">
            <button onClick={() => onScopeChange("all")} className={`px-3 py-2 rounded-xl text-sm ${scope === "all" ? "bg-red-50 text-red-600" : "bg-gray-50 text-gray-600"}`}>Tất cả từ đã lưu</button>
            <button onClick={() => onScopeChange("folder")} className={`px-3 py-2 rounded-xl text-sm ${scope === "folder" ? "bg-red-50 text-red-600" : "bg-gray-50 text-gray-600"}`}>Theo folder</button>
            <button onClick={() => onScopeChange("topic")} className={`px-3 py-2 rounded-xl text-sm ${scope === "topic" ? "bg-red-50 text-red-600" : "bg-gray-50 text-gray-600"}`}>Theo topic</button>
          </div>

          {scope === "folder" && (
            <select value={scopeId} onChange={(e) => onScopeIdChange(e.target.value)} className="w-full border rounded-xl px-3 py-2 text-sm">
              <option value="">-- Chọn folder --</option>
              {folders.map((f) => <option key={f._id} value={f._id}>{f.name}</option>)}
            </select>
          )}

          {scope === "topic" && (
            <select value={scopeId} onChange={(e) => onScopeIdChange(e.target.value)} className="w-full border rounded-xl px-3 py-2 text-sm">
              <option value="">-- Chọn topic --</option>
              {topics.map((t) => <option key={t._id} value={t._id}>{t.name}</option>)}
            </select>
          )}

          <div className="border rounded-xl p-3 bg-gray-50">
            <p className="text-sm font-medium text-gray-700 mb-2">Preview ({previewTotal} từ)</p>
            {previewLoading ? (
              <p className="text-sm text-gray-400">Đang tải preview...</p>
            ) : preview.length === 0 ? (
              <p className="text-sm text-gray-400">Không có từ nào trong lựa chọn này.</p>
            ) : (
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {preview.map((w) => (
                  <div key={w._id} className="text-sm text-gray-700 flex items-center gap-2">
                    <span className="font-semibold">{w.word}</span>
                    {w.pinyin && <span className="text-gray-400 italic">{w.pinyin}</span>}
                    <span className="text-gray-500">- {w.meaning}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg border hover:bg-gray-50">Huỷ</button>
            <button onClick={onStart} disabled={previewTotal === 0 || (scope !== "all" && !scopeId)} className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60">
              Bắt đầu luyện
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function VocabularyPage() {
  const navigate = useNavigate();
  // ── master data ───────────────────────────────────────────────────────────
  const [masterTopics, setMasterTopics] = useState([]);
  const [masterVocabs, setMasterVocabs] = useState([]);
  const [masterTotal, setMasterTotal] = useState(0);
  const [masterPage, setMasterPage] = useState(1);
  const [masterSearch, setMasterSearch] = useState("");
  const [masterSearchInput, setMasterSearchInput] = useState("");

  // ── user data ─────────────────────────────────────────────────────────────
  const [folders, setFolders] = useState([]);
  const [savedVocabs, setSavedVocabs] = useState([]);
  const [savedTotal, setSavedTotal] = useState(0);
  const [savedPage, setSavedPage] = useState(1);
  // Set of masterVocabIds already saved by user (for toggle display)
  const [savedMasterIds, setSavedMasterIds] = useState(new Set());

  // ── view state ────────────────────────────────────────────────────────────
  // view: "browse" | "saved"
  const [view, setView] = useState("browse");
  const [selectedTopicId, setSelectedTopicId] = useState(null); // for browse
  const [selectedFolderId, setSelectedFolderId] = useState(null); // for saved

  // ── ui state ──────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [newFolderName, setNewFolderName] = useState("");
  const [addingFolder, setAddingFolder] = useState(false);
  const [renamingFolderId, setRenamingFolderId] = useState(null);
  const [selectedMasterIds, setSelectedMasterIds] = useState(new Set());
  const [selectedSavedIds, setSelectedSavedIds] = useState(new Set());
  const [bulkFolderId, setBulkFolderId] = useState("");

  const [practiceOpen, setPracticeOpen] = useState(false);
  const [practiceScope, setPracticeScope] = useState("all"); // all | folder | topic
  const [practiceScopeId, setPracticeScopeId] = useState("");
  const [practicePreview, setPracticePreview] = useState([]);
  const [practicePreviewTotal, setPracticePreviewTotal] = useState(0);
  const [practiceLoading, setPracticeLoading] = useState(false);

  // ── load master topics + folders on mount ─────────────────────────────────
  const loadTopics = useCallback(async () => {
    try {
      const res = await getMasterTopics();
      setMasterTopics(res?.data || []);
    } catch {
      toast.error("Không thể tải danh sách chủ đề");
    }
  }, []);

  const loadFolders = useCallback(async () => {
    try {
      const res = await getFolders();
      setFolders(res?.data || []);
    } catch {
      toast.error("Không thể tải folder");
    }
  }, []);

  useEffect(() => {
    loadTopics();
    loadFolders();
  }, [loadTopics, loadFolders]);

  // ── load saved vocab master IDs (to show save toggle correctly) ───────────
  const loadSavedIds = useCallback(async () => {
    try {
      const res = await getUserVocabs({ limit: 1000 });
      const ids = new Set((res?.data || []).map((v) => String(v.masterVocabId?._id || v.masterVocabId)));
      setSavedMasterIds(ids);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadSavedIds(); }, [loadSavedIds]);

  // ── load master vocab when browsing ──────────────────────────────────────
  const loadMasterVocabs = useCallback(async () => {
    if (view !== "browse") return;
    setLoading(true);
    try {
      const params = { page: masterPage, limit: LIMIT };
      if (selectedTopicId) params.topicId = selectedTopicId;
      if (masterSearch) params.q = masterSearch;
      const res = await getMasterVocabList(params);
      setMasterVocabs(res?.data || []);
      setMasterTotal(res?.total || 0);
    } catch {
      toast.error("Không thể tải từ vựng");
    } finally {
      setLoading(false);
    }
  }, [view, selectedTopicId, masterPage, masterSearch]);

  useEffect(() => { loadMasterVocabs(); }, [loadMasterVocabs]);

  // ── load saved vocab when viewing saved ───────────────────────────────────
  const loadSavedVocabs = useCallback(async () => {
    if (view !== "saved") return;
    setLoading(true);
    try {
      const params = { page: savedPage, limit: LIMIT };
      if (selectedFolderId) params.folderId = selectedFolderId;
      const res = await getUserVocabs(params);
      setSavedVocabs(res?.data || []);
      setSavedTotal(res?.total || 0);
    } catch {
      toast.error("Không thể tải từ đã lưu");
    } finally {
      setLoading(false);
    }
  }, [view, selectedFolderId, savedPage]);

  useEffect(() => { loadSavedVocabs(); }, [loadSavedVocabs]);

  // ── handlers ──────────────────────────────────────────────────────────────
  const handleSave = useCallback(async (masterVocabId) => {
    setSavingId(masterVocabId);
    try {
      await saveVocab(masterVocabId);
      setSavedMasterIds((prev) => new Set([...prev, String(masterVocabId)]));
      toast.success("Đã lưu từ vựng");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Lưu thất bại");
    } finally {
      setSavingId(null);
    }
  }, []);

  const handleUnsaveFromBrowse = useCallback(async (masterVocabId) => {
    setSavingId(masterVocabId);
    try {
      // Need to find userVocabId — fetch by masterVocabId
      const res = await getUserVocabs({ limit: 1000 });
      const entry = (res?.data || []).find((v) => String(v.masterVocabId?._id || v.masterVocabId) === String(masterVocabId));
      if (entry) {
        await deleteUserVocab(entry._id);
        setSavedMasterIds((prev) => { const next = new Set(prev); next.delete(String(masterVocabId)); return next; });
        toast.success("Đã bỏ lưu");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Thất bại");
    } finally {
      setSavingId(null);
    }
  }, []);

  const handleUnsaveFromSaved = useCallback(async (userVocabId) => {
    try {
      await deleteUserVocab(userVocabId);
      setSavedVocabs((prev) => prev.filter((v) => v._id !== userVocabId));
      setSavedTotal((prev) => prev - 1);
      // Also update savedMasterIds
      loadSavedIds();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Thất bại");
    }
  }, [loadSavedIds]);

  const handleCreateFolder = useCallback(async () => {
    if (!newFolderName.trim()) return;
    try {
      await createFolder(newFolderName.trim());
      setNewFolderName("");
      setAddingFolder(false);
      loadFolders();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Tạo folder thất bại");
    }
  }, [newFolderName, loadFolders]);

  const handleRenameFolder = useCallback(async (id, name) => {
    if (!name.trim()) { setRenamingFolderId(null); return; }
    try {
      await updateFolder(id, name.trim());
      setRenamingFolderId(null);
      loadFolders();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Đổi tên thất bại");
    }
  }, [loadFolders]);

  const handleDeleteFolder = useCallback(async (id) => {
    if (!window.confirm("Xóa folder này?")) return;
    try {
      await deleteFolder(id);
      if (selectedFolderId === id) setSelectedFolderId(null);
      loadFolders();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Xóa thất bại");
    }
  }, [selectedFolderId, loadFolders]);

  const selectTopic = useCallback((id) => {
    setView("browse");
    setSelectedTopicId(id);
    setSelectedMasterIds(new Set());
    setMasterPage(1);
    setMasterSearch("");
    setMasterSearchInput("");
  }, []);

  const selectFolder = useCallback((id) => {
    setView("saved");
    setSelectedFolderId(id);
    setSelectedSavedIds(new Set());
    setSavedPage(1);
  }, []);

  const handleMasterSearch = useCallback(() => {
    setMasterSearch(masterSearchInput);
    setMasterPage(1);
  }, [masterSearchInput]);

  const toggleMasterSelect = useCallback((id) => {
    setSelectedMasterIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSavedSelect = useCallback((id) => {
    setSelectedSavedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleBulkSave = useCallback(async () => {
    if (selectedMasterIds.size === 0) return;
    try {
      await Promise.all(Array.from(selectedMasterIds).map((id) => saveVocab(id, bulkFolderId || null)));
      toast.success(`Đã lưu ${selectedMasterIds.size} từ`);
      setSelectedMasterIds(new Set());
      loadSavedIds();
      loadSavedVocabs();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Lưu hàng loạt thất bại");
    }
  }, [selectedMasterIds, bulkFolderId, loadSavedIds, loadSavedVocabs]);

  const handleBulkMoveToFolder = useCallback(async () => {
    if (!bulkFolderId || selectedSavedIds.size === 0) return;
    try {
      await Promise.all(Array.from(selectedSavedIds).map((id) => addToFolder(bulkFolderId, id)));
      toast.success(`Đã chuyển ${selectedSavedIds.size} từ vào folder`);
      setSelectedSavedIds(new Set());
    } catch (err) {
      toast.error(err?.response?.data?.message || "Chuyển folder thất bại");
    }
  }, [bulkFolderId, selectedSavedIds]);

  const loadPracticePreview = useCallback(async () => {
    try {
      setPracticeLoading(true);
      if (practiceScope === "all") {
        const res = await getUserVocabs({ page: 1, limit: 8 });
        const all = await getUserVocabs({ page: 1, limit: 1000 });
        const words = (res?.data || []).map((v) => ({ ...v.masterVocabId, _id: v._id }));
        setPracticePreview(words);
        setPracticePreviewTotal(all?.total || 0);
      } else if (practiceScope === "folder") {
        if (!practiceScopeId) {
          setPracticePreview([]);
          setPracticePreviewTotal(0);
          return;
        }
        const res = await getUserVocabs({ page: 1, limit: 8, folderId: practiceScopeId });
        const words = (res?.data || []).map((v) => ({ ...v.masterVocabId, _id: v._id }));
        setPracticePreview(words);
        setPracticePreviewTotal(res?.total || 0);
      } else {
        if (!practiceScopeId) {
          setPracticePreview([]);
          setPracticePreviewTotal(0);
          return;
        }
        const res = await getUserVocabs({ page: 1, limit: 1000 });
        const filtered = (res?.data || []).filter((v) => String(v.masterVocabId?.topicId?._id || v.masterVocabId?.topicId) === String(practiceScopeId));
        setPracticePreview(filtered.slice(0, 8).map((v) => ({ ...v.masterVocabId, _id: v._id })));
        setPracticePreviewTotal(filtered.length);
      }
    } catch {
      setPracticePreview([]);
      setPracticePreviewTotal(0);
    } finally {
      setPracticeLoading(false);
    }
  }, [practiceScope, practiceScopeId]);

  useEffect(() => {
    if (!practiceOpen) return;
    loadPracticePreview();
  }, [practiceOpen, loadPracticePreview]);

  const handleStartPractice = useCallback(() => {
    const query = new URLSearchParams();
    query.set("scope", practiceScope);
    if (practiceScope !== "all") query.set("scopeId", practiceScopeId);
    navigate(`/vocabulary/learn?${query.toString()}`);
  }, [navigate, practiceScope, practiceScopeId]);

  const masterTotalPages = Math.ceil(masterTotal / LIMIT);
  const savedTotalPages = Math.ceil(savedTotal / LIMIT);

  // ── header ────────────────────────────────────────────────────────────────
  const pageHeader = (
    <div className="flex items-center gap-3">
      <BookOpen size={18} className="text-red-600" />
      <h1 className="text-base font-bold text-gray-900">Từ vựng</h1>
    </div>
  );

  return (
    <VocabLayout header={pageHeader} mainClassName="flex-1 flex min-h-0 overflow-hidden">
      {/* ── Left Sidebar ───────────────────────────────────────────────────── */}
      <aside className="w-60 shrink-0 bg-white border-r flex flex-col overflow-y-auto">

        {/* Section: Từ đã lưu */}
        <div className="px-3 pt-4 pb-2">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-2 mb-1">Từ đã lưu</p>
          <button
            onClick={() => selectFolder(null)}
            className={`w-full text-left flex items-center justify-between px-3 py-2 rounded-xl text-sm transition-colors ${
              view === "saved" && selectedFolderId === null
                ? "bg-red-50 text-red-600 font-medium"
                : "text-gray-700 hover:bg-gray-100"
            }`}
          >
            <span className="flex items-center gap-2">📚 Tất cả</span>
            <span className="text-xs text-gray-400">{savedTotal > 0 ? savedTotal : ""}</span>
          </button>

          {folders.map((f) => (
            <div key={f._id}>
              {renamingFolderId === f._id ? (
                <FolderRenameInline
                  folder={f}
                  onSave={(name) => handleRenameFolder(f._id, name)}
                  onCancel={() => setRenamingFolderId(null)}
                />
              ) : (
                <div className={`group flex items-center rounded-xl transition-colors ${
                  view === "saved" && selectedFolderId === f._id
                    ? "bg-red-50"
                    : "hover:bg-gray-100"
                }`}>
                  <button
                    onClick={() => selectFolder(f._id)}
                    className={`flex-1 text-left flex items-center gap-2 px-3 py-2 text-sm ${
                      view === "saved" && selectedFolderId === f._id ? "text-red-600 font-medium" : "text-gray-700"
                    }`}
                  >
                    📁 {f.name}
                  </button>
                  <button onClick={() => setRenamingFolderId(f._id)} className="hidden group-hover:flex p-1 text-gray-300 hover:text-blue-500">
                    <Pencil size={12} />
                  </button>
                  <button onClick={() => handleDeleteFolder(f._id)} className="hidden group-hover:flex p-1 text-gray-300 hover:text-red-500 mr-1">
                    <Trash2 size={12} />
                  </button>
                </div>
              )}
            </div>
          ))}

          {/* Add folder */}
          {addingFolder ? (
            <div className="flex items-center gap-1 px-2 py-1 mt-1">
              <input
                autoFocus
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleCreateFolder(); if (e.key === "Escape") setAddingFolder(false); }}
                placeholder="Tên folder..."
                className="flex-1 text-sm border-b border-red-400 outline-none bg-transparent py-0.5"
              />
              <button onClick={handleCreateFolder} className="text-green-600 text-xs font-medium">OK</button>
              <button onClick={() => setAddingFolder(false)} className="text-gray-400"><X size={13} /></button>
            </div>
          ) : (
            <button
              onClick={() => setAddingFolder(true)}
              className="flex items-center gap-2 px-3 py-1.5 mt-1 text-sm text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl w-full transition-colors"
            >
              <FolderPlus size={13} /> Tạo folder
            </button>
          )}
        </div>

        <div className="mx-3 border-t my-2" />

        {/* Section: Khám phá chủ đề */}
        <div className="px-3 pb-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-2 mb-1">Khám phá chủ đề</p>
          <button
            onClick={() => selectTopic(null)}
            className={`w-full text-left flex items-center justify-between px-3 py-2 rounded-xl text-sm transition-colors ${
              view === "browse" && selectedTopicId === null
                ? "bg-red-50 text-red-600 font-medium"
                : "text-gray-700 hover:bg-gray-100"
            }`}
          >
            🌐 Tất cả từ
          </button>
          {masterTopics.map((t) => (
            <button
              key={t._id}
              onClick={() => selectTopic(t._id)}
              className={`w-full text-left flex items-center justify-between px-3 py-2 rounded-xl text-sm transition-colors ${
                view === "browse" && selectedTopicId === t._id
                  ? "bg-red-50 text-red-600 font-medium"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              <span>{t.icon ? `${t.icon} ` : ""}{t.name}</span>
              <ChevronRight size={13} className="text-gray-300" />
            </button>
          ))}
        </div>
      </aside>

      {/* ── Main Content ───────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Toolbar */}
        <div className="bg-white border-b px-6 py-3 flex items-center gap-3 shrink-0">
          <h2 className="font-semibold text-gray-800 text-sm">
            {view === "browse"
              ? (selectedTopicId ? masterTopics.find((t) => t._id === selectedTopicId)?.name || "Chủ đề" : "Tất cả từ vựng")
              : (selectedFolderId ? folders.find((f) => f._id === selectedFolderId)?.name || "Folder" : "Tất cả từ đã lưu")
            }
          </h2>
          <span className="text-xs text-gray-400">({view === "browse" ? masterTotal : savedTotal} từ)</span>

          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => setPracticeOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-600 text-white text-xs font-medium hover:bg-red-700 transition-colors"
            >
              <Brain size={13} /> Luyện ngay
            </button>
            {view === "browse" && (
              <div className="flex items-center border rounded-xl overflow-hidden">
                <input
                  value={masterSearchInput}
                  onChange={(e) => setMasterSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleMasterSearch()}
                  placeholder="Tìm từ..."
                  className="px-3 py-1.5 text-sm outline-none w-40"
                />
                <button onClick={handleMasterSearch} className="px-2.5 py-1.5 bg-gray-50 hover:bg-gray-100 border-l">
                  <Search size={14} className="text-gray-400" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {view === "browse" && selectedMasterIds.size > 0 && (
            <div className="mb-4 bg-red-50 border border-red-100 rounded-2xl px-4 py-3 flex items-center gap-3 flex-wrap">
              <span className="text-sm text-red-700 font-medium">Đã chọn {selectedMasterIds.size} từ</span>
              <select value={bulkFolderId} onChange={(e) => setBulkFolderId(e.target.value)} className="border rounded-xl px-3 py-1.5 text-sm">
                <option value="">Lưu không vào folder</option>
                {folders.map((f) => <option key={f._id} value={f._id}>{f.name}</option>)}
              </select>
              <button onClick={handleBulkSave} className="px-3 py-1.5 rounded-xl bg-red-600 text-white text-sm hover:bg-red-700">Lưu hàng loạt</button>
              <button onClick={() => setSelectedMasterIds(new Set())} className="px-3 py-1.5 rounded-xl border text-sm hover:bg-white">Bỏ chọn</button>
            </div>
          )}

          {view === "saved" && selectedSavedIds.size > 0 && (
            <div className="mb-4 bg-blue-50 border border-blue-100 rounded-2xl px-4 py-3 flex items-center gap-3 flex-wrap">
              <span className="text-sm text-blue-700 font-medium">Đã chọn {selectedSavedIds.size} từ</span>
              <select value={bulkFolderId} onChange={(e) => setBulkFolderId(e.target.value)} className="border rounded-xl px-3 py-1.5 text-sm">
                <option value="">Chọn folder để chuyển</option>
                {folders.map((f) => <option key={f._id} value={f._id}>{f.name}</option>)}
              </select>
              <button onClick={handleBulkMoveToFolder} disabled={!bulkFolderId} className="px-3 py-1.5 rounded-xl bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-60">Chuyển vào folder</button>
              <button onClick={() => setSelectedSavedIds(new Set())} className="px-3 py-1.5 rounded-xl border text-sm hover:bg-white">Bỏ chọn</button>
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 h-36 animate-pulse" />
              ))}
            </div>
          ) : view === "browse" ? (
            <>
              {masterVocabs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-gray-400">
                  <BookOpen size={40} className="mb-3 opacity-30" />
                  <p className="text-sm">Chưa có từ vựng nào trong chủ đề này</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4">
                  {masterVocabs.map((word) => (
                    <MasterWordCard
                      key={word._id}
                      word={word}
                      saved={savedMasterIds.has(String(word._id))}
                      onSave={handleSave}
                      onUnsave={handleUnsaveFromBrowse}
                      savingId={savingId}
                      checked={selectedMasterIds.has(word._id)}
                      onToggle={toggleMasterSelect}
                    />
                  ))}
                </div>
              )}
              {masterTotalPages > 1 && (
                <div className="flex justify-center gap-2 mt-6">
                  <button disabled={masterPage <= 1} onClick={() => setMasterPage((p) => p - 1)} className="px-3 py-1.5 text-sm border rounded-xl hover:bg-gray-50 disabled:opacity-40">← Trước</button>
                  <span className="px-3 py-1.5 text-sm text-gray-500">{masterPage} / {masterTotalPages}</span>
                  <button disabled={masterPage >= masterTotalPages} onClick={() => setMasterPage((p) => p + 1)} className="px-3 py-1.5 text-sm border rounded-xl hover:bg-gray-50 disabled:opacity-40">Sau →</button>
                </div>
              )}
            </>
          ) : (
            <>
              {savedVocabs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-gray-400">
                  <Bookmark size={40} className="mb-3 opacity-30" />
                  <p className="text-sm">Chưa lưu từ nào</p>
                  <button onClick={() => setView("browse")} className="mt-3 text-sm text-red-600 hover:underline">
                    Khám phá chủ đề để lưu từ →
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4">
                  {savedVocabs.map((item) => (
                    <SavedWordCard
                      key={item._id}
                      item={item}
                      onUnsave={handleUnsaveFromSaved}
                      checked={selectedSavedIds.has(item._id)}
                      onToggle={toggleSavedSelect}
                    />
                  ))}
                </div>
              )}
              {savedTotalPages > 1 && (
                <div className="flex justify-center gap-2 mt-6">
                  <button disabled={savedPage <= 1} onClick={() => setSavedPage((p) => p - 1)} className="px-3 py-1.5 text-sm border rounded-xl hover:bg-gray-50 disabled:opacity-40">← Trước</button>
                  <span className="px-3 py-1.5 text-sm text-gray-500">{savedPage} / {savedTotalPages}</span>
                  <button disabled={savedPage >= savedTotalPages} onClick={() => setSavedPage((p) => p + 1)} className="px-3 py-1.5 text-sm border rounded-xl hover:bg-gray-50 disabled:opacity-40">Sau →</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <PracticeStartModal
        open={practiceOpen}
        onClose={() => setPracticeOpen(false)}
        scope={practiceScope}
        scopeId={practiceScopeId}
        onScopeChange={(scope) => {
          setPracticeScope(scope);
          setPracticeScopeId("");
        }}
        onScopeIdChange={setPracticeScopeId}
        preview={practicePreview}
        previewTotal={practicePreviewTotal}
        previewLoading={practiceLoading}
        folders={folders}
        topics={masterTopics}
        onStart={handleStartPractice}
      />
    </VocabLayout>
  );
}